import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { classNames } from "primereact/utils";
import styles from "./WorkoutPlanDialog.module.scss";
import { AppDialog } from "../AppDialog/AppDialog";
import { useKeyboardAwareDialog } from "../AppDialog/hooks/useKeyboardAwareDialog";
import { ReorderDialog } from "../ReorderDialog/ReorderDialog";
import { WorkoutPlanDialogSkeleton } from "./WorkoutPlanDialogSkeleton";
import { WorkoutPlanExerciseCard } from "./components/WorkoutPlanExerciseCard";
import { WorkoutMuscleSplit } from "./components/WorkoutMuscleSplit";
import { WorkoutPlanExercisePicker } from "./components/WorkoutPlanExercisePicker";
import { WorkoutPlanNameField } from "./components/WorkoutPlanNameField";
import { WorkoutPlanTemplateLoader } from "./components/WorkoutPlanTemplateLoader";
import { useWorkoutPlanDialog } from "./hooks/useWorkoutPlanDialog";
import {
  IWorkoutPlanExerciseViewModel,
  WorkoutPlanDialogMode,
  WorkoutSection,
} from "./models/workoutPlanViewModel";
import {
  WORKOUT_SECTION_ORDER,
  workoutSectionLabel,
} from "./components/SectionPicker";
import { CreateExerciseSheet } from "./components/CreateExerciseSheet";
import { Icon, IconType } from "../../icons/Icon";
import { showCustomConfirmDialog } from "../dialogs/AppConfirmDialog";

const LINEAGE_FALLBACK_NAME = "Тренировка";

interface IWorkoutPlanDialogProps {
  visible: boolean;
  onHide: () => void;
  eventId?: number;
  /** Open a standalone (event-less) workout directly by its plan id. */
  planId?: number;
  /**
   * The user whose history the workout is read against (ПРЕДИШНА, personal records), when
   * the page knows it — the admin user-profile workouts page. See useWorkoutPlanDialog.
   */
  userId?: number;
  registeredUserName?: string;
  eventDateLabel?: string;
  eventTimeLabel?: string;
  mode: WorkoutPlanDialogMode;
}

export const WorkoutPlanDialog = ({
  visible,
  onHide,
  eventId,
  planId,
  userId,
  registeredUserName,
  eventDateLabel,
  eventTimeLabel,
  mode,
}: IWorkoutPlanDialogProps) => {
  const {
    plan,
    isLoading,
    isReadOnly,
    exercises,
    updateWorkoutPlanName,
    addExerciseFromCatalog,
    addExerciseByName,
    removeExercise,
    updateExerciseNotes,
    updateExerciseRestTimer,
    updateExerciseUseAutomaticTimer,
    updateExerciseSupersetLetter,
    updateExerciseType,
    updateExerciseMuscleGroups,
    updateExerciseResistanceBand,
    updateExerciseSection,
    updateExerciseDistanceUnit,
    addSet,
    updateSet,
    removeSet,
    toggleSetCompleted,
    loadTemplate,
    reorderExercises,
    deleteWorkout,
  } = useWorkoutPlanDialog({ eventId, planId, userId, mode, isOpen: visible });

  const [isReorderOpen, setIsReorderOpen] = useState(false);
  // Warm-up / cool-down the user opened that have no exercises yet. A section with
  // exercises is always shown; an empty one only while it is in this list.
  const [openedSections, setOpenedSections] = useState<WorkoutSection[]>([]);
  const [collapsedSections, setCollapsedSections] = useState<WorkoutSection[]>([]);
  // "+ Създай ново упражнение" asks for the type and band option first.
  const [createRequest, setCreateRequest] = useState<{
    name: string;
    section: WorkoutSection;
  } | null>(null);

  // Section display state belongs to one workout; start clean for the next one.
  useEffect(() => {
    setOpenedSections([]);
    setCollapsedSections([]);
  }, [plan?.id]);

  const exercisesBySection = useMemo(() => {
    const grouped = new Map<WorkoutSection, IWorkoutPlanExerciseViewModel[]>(
      WORKOUT_SECTION_ORDER.map((section) => [section, []]),
    );
    for (const exercise of exercises) {
      (grouped.get(exercise.section) ?? grouped.get(WorkoutSection.Workout)!).push(exercise);
    }
    return grouped;
  }, [exercises]);

  const isSectionVisible = (section: WorkoutSection) =>
    section === WorkoutSection.Workout ||
    (exercisesBySection.get(section)?.length ?? 0) > 0 ||
    openedSections.includes(section);
  // Until a warm-up or cool-down is in play the workout looks exactly as it always
  // did: one list, no section headers.
  const usesSections =
    isSectionVisible(WorkoutSection.WarmUp) || isSectionVisible(WorkoutSection.CoolDown);
  const canAddSections = !isReadOnly && exercises.length > 0;
  // The order the workout runs in — what the list shows and the reorder dialog edits.
  const orderedExercises = WORKOUT_SECTION_ORDER.flatMap(
    (section) => exercisesBySection.get(section) ?? [],
  );

  const openSection = (section: WorkoutSection) => {
    setOpenedSections((current) => (current.includes(section) ? current : [...current, section]));
    setCollapsedSections((current) => current.filter((s) => s !== section));
  };
  const hideSection = (section: WorkoutSection) =>
    setOpenedSections((current) => current.filter((s) => s !== section));
  const toggleCollapsed = (section: WorkoutSection) =>
    setCollapsedSections((current) =>
      current.includes(section) ? current.filter((s) => s !== section) : [...current, section],
    );

  // Keep the whole dialog inside the visible area while the mobile keyboard is up
  // (typing in the exercise picker / set inputs) instead of letting iOS pan it away.
  const { dialogStyle, maskClassName } = useKeyboardAwareDialog(visible);

  const subTextParts = [
    eventDateLabel,
    eventTimeLabel,
    registeredUserName,
  ].filter(Boolean);

  const lineageLabel =
    plan?.lineageNumber && plan.lineageTotalCount
      ? `#${plan.lineageNumber} от ${plan.lineageTotalCount} в „${plan.lineageRootName?.trim() || LINEAGE_FALLBACK_NAME}"`
      : null;

  const canReorder = !isReadOnly && exercises.length >= 2;
  // Deleting is available for both standalone and event-linked workouts, to the plan's
  // owner/registered trainee and to an admin — same actors who may already edit the plan.
  const canDelete = !!plan;

  const handleDelete = () => {
    showCustomConfirmDialog(
      "Сигурни ли сте, че искате да изтриете тази тренировка?",
      async () => {
        await deleteWorkout();
        onHide();
      },
      undefined,
      "warning",
      "Изтрий",
      "Отказ",
    );
  };

  const header = (
    <div className={styles.header_block}>
      <WorkoutPlanNameField
        value={plan?.name ?? null}
        isReadOnly={isReadOnly}
        onCommit={updateWorkoutPlanName}
      />
      {subTextParts.length > 0 && (
        <div className={styles.sub}>{subTextParts.join(" · ")}</div>
      )}
      {(lineageLabel || canReorder || canDelete) && (
        <div className={styles.lineage_row}>
          {lineageLabel && (
            <span className={styles.lineage_chip} title={lineageLabel}>
              {lineageLabel}
            </span>
          )}
          {canReorder && (
            <button
              type="button"
              className={styles.reorder_btn}
              onClick={() => setIsReorderOpen(true)}
              aria-label="Пренареди упражнения"
              title="Пренареди упражнения"
            >
              <Icon
                type={IconType.DragHandle}
                width={14}
                height={14}
                color="rgba(160, 158, 220, 0.95)"
              />
              <span>Пренареди</span>
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className={styles.delete_btn}
              onClick={handleDelete}
              aria-label="Изтрий тренировката"
              title="Изтрий тренировката"
            >
              <Icon
                type={IconType.DeleteIcon2}
                width={14}
                height={14}
                color="rgba(240, 150, 150, 0.95)"
              />
              <span>Изтрий</span>
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderExerciseCard = (exercise: IWorkoutPlanExerciseViewModel) => (
    <motion.div
      key={exercise.clientKey ?? exercise.id}
      layout
      initial={{ opacity: 0, scale: 0.96, height: 0 }}
      animate={{ opacity: 1, scale: 1, height: "auto" }}
      exit={{ opacity: 0, scale: 0.96, height: 0 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      style={{ overflow: "hidden" }}
    >
      <WorkoutPlanExerciseCard
        exercise={exercise}
        isReadOnly={isReadOnly}
        onRemoveExercise={removeExercise}
        onUpdateNotes={updateExerciseNotes}
        onUpdateRestTimer={updateExerciseRestTimer}
        onUpdateUseAutomaticTimer={updateExerciseUseAutomaticTimer}
        onUpdateSupersetLetter={updateExerciseSupersetLetter}
        onUpdateExerciseType={updateExerciseType}
        onUpdateMuscleGroups={updateExerciseMuscleGroups}
        onUpdateResistanceBand={updateExerciseResistanceBand}
        onUpdateDistanceUnit={updateExerciseDistanceUnit}
        onUpdateSection={updateExerciseSection}
        showSectionChip={usesSections}
        onAddSet={addSet}
        onUpdateSet={updateSet}
        onToggleSetCompleted={toggleSetCompleted}
        onRemoveSet={removeSet}
      />
    </motion.div>
  );

  const renderPicker = (section: WorkoutSection) => (
    <WorkoutPlanExercisePicker
      eventId={eventId}
      planId={plan?.id}
      onPickFromCatalog={(exerciseId, exerciseName) =>
        addExerciseFromCatalog(exerciseId, exerciseName, section)
      }
      onCreateAndAdd={(name) => setCreateRequest({ name, section })}
      canCreateExercise={mode === "admin"}
      noHistoryLabel={mode === "admin" ? "ново за клиента" : "ново за вас"}
    />
  );

  const renderSection = (section: WorkoutSection) => {
    const sectionExercises = exercisesBySection.get(section) ?? [];
    const isCollapsed = collapsedSections.includes(section);
    const label = workoutSectionLabel(section);
    return (
      <div className={styles.section} key={section}>
        <div className={styles.section_head}>
          <button
            type="button"
            className={styles.section_toggle}
            onClick={() => toggleCollapsed(section)}
            aria-expanded={!isCollapsed}
            aria-label={`${label}: ${isCollapsed ? "покажи" : "скрий"} упражненията`}
          >
            <span
              className={classNames(styles.section_chevron, {
                [styles.section_chevron_collapsed]: isCollapsed,
              })}
              aria-hidden
            >
              ▾
            </span>
            <span className={styles.section_title}>{label}</span>
            <span className={styles.section_count}>{sectionExercises.length}</span>
          </button>
          {/* Only an empty warm-up / cool-down can go away; one with exercises can
              only be collapsed, so nothing is ever hidden by accident. */}
          {!isReadOnly &&
            section !== WorkoutSection.Workout &&
            sectionExercises.length === 0 && (
              <button
                type="button"
                className={styles.section_hide}
                onClick={() => hideSection(section)}
              >
                Махни
              </button>
            )}
        </div>
        {!isCollapsed && (
          <>
            <AnimatePresence initial={false}>
              {sectionExercises.map(renderExerciseCard)}
            </AnimatePresence>
            {sectionExercises.length === 0 && (
              <div className={styles.section_empty}>Все още няма упражнения.</div>
            )}
            {!isReadOnly && renderPicker(section)}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <AppDialog
        visible={visible}
        onHide={onHide}
        header={header}
        className={styles.dialog}
        contentClassName={styles.dialog_content}
        maskClassName={maskClassName}
        style={dialogStyle}
      >
        <div className={styles.body}>
          {isLoading ? (
            <WorkoutPlanDialogSkeleton />
          ) : (
            <>
              <WorkoutMuscleSplit exercises={exercises} />

              {canAddSections && !isSectionVisible(WorkoutSection.WarmUp) && (
                <div className={styles.section_add_row}>
                  <button
                    type="button"
                    className={styles.section_add}
                    onClick={() => openSection(WorkoutSection.WarmUp)}
                  >
                    + Загрявка
                  </button>
                </div>
              )}

              {usesSections ? (
                WORKOUT_SECTION_ORDER.filter(isSectionVisible).map((section) =>
                  renderSection(section),
                )
              ) : (
                <>
                  <AnimatePresence initial={false}>
                    {exercises.map(renderExerciseCard)}
                  </AnimatePresence>

              {!isReadOnly && exercises.length === 0 && (
                <motion.div
                  className={styles.empty_card}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, ease: "easeOut" }}
                >
                  <div className={styles.empty_card_header}>
                    <div className={styles.empty_card_orb}>
                      <Icon
                        type={IconType.Dumbell}
                        color="rgba(180,178,235,0.95)"
                      />
                    </div>
                    <div className={styles.empty_card_title}>
                      Все още няма упражнения
                    </div>
                  </div>

                  {plan && plan.registeredUserId > 0 && (
                    <>
                      <div className={styles.empty_option}>
                        <div className={styles.empty_option_heading}>
                          <div className={styles.empty_option_label}>
                            Копирай предишна тренировка
                          </div>
                          <div className={styles.empty_option_desc}>
                            Заредете упражненията и сериите от предишен запис
                            като отправна точка.
                          </div>
                        </div>
                        <WorkoutPlanTemplateLoader
                          userId={plan.registeredUserId}
                          excludePlanId={plan.id}
                          visible={visible}
                          onLoad={loadTemplate}
                        />
                      </div>

                      <div className={styles.empty_option_divider} aria-hidden>
                        <span>или</span>
                      </div>
                    </>
                  )}

                  <div className={styles.empty_option}>
                    <div className={styles.empty_option_heading}>
                      <div className={styles.empty_option_label}>
                        Започни от нулата
                      </div>
                      <div className={styles.empty_option_desc}>
                        Добавете първото упражнение от полето по-долу и
                        изградете тренировката стъпка по стъпка.
                      </div>
                    </div>
                    <div className={styles.empty_option_arrow} aria-hidden>
                      ↓
                    </div>
                  </div>
                </motion.div>
              )}

                  {!isReadOnly && renderPicker(WorkoutSection.Workout)}
                </>
              )}

              {canAddSections && !isSectionVisible(WorkoutSection.CoolDown) && (
                <div className={`${styles.section_add_row} ${styles.section_add_row_bottom}`}>
                  <button
                    type="button"
                    className={styles.section_add}
                    onClick={() => openSection(WorkoutSection.CoolDown)}
                  >
                    + Разпускане
                  </button>
                </div>
              )}

              {isReadOnly && exercises.length === 0 && (
                <div className={styles.empty}>
                  Треньорът още не е подготвил план за тази тренировка.
                </div>
              )}
            </>
          )}
        </div>
      </AppDialog>
      <ReorderDialog
        visible={isReorderOpen}
        onHide={() => setIsReorderOpen(false)}
        title="Пренареди упражнения"
        items={orderedExercises.map((e) => ({
          id: e.id,
          name:
            usesSections && e.section !== WorkoutSection.Workout
              ? `${workoutSectionLabel(e.section)} · ${e.exerciseName}`
              : e.exerciseName,
        }))}
        onSave={async (orderedIds) => {
          await reorderExercises(orderedIds);
        }}
      />
      <CreateExerciseSheet
        name={createRequest?.name ?? null}
        onClose={() => setCreateRequest(null)}
        onCreate={(name, options) =>
          addExerciseByName(name, options, createRequest?.section ?? WorkoutSection.Workout)
        }
      />
    </>
  );
};
